import { OpenNew } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuous } from '@/components/loaders/CircularProgressContinuous'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { selectDisplayCostPerSeatInCents } from '@/account/store/selectors'
import { useMembersRosterData } from '@/organizations/hooks/useMembersRosterData'
import { OrgBillingStats } from '@/organizations/views/OrgBillingStats'
import SectionHeader from '@/settings/components/SectionHeader'
import { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'
import { formatCentsToDollarsCompact } from '@/util/money'

import { SettingsPanelShell } from '../components/SettingsPanelShell'

// Marketing pricing page, matching PricingInformationLink. The design renders this as a plain button
// (not a link), so we navigate on click rather than reusing that anchor component.
const kBrowseAllPlansUrl = 'https://www.paxton.ai/pricing'

/**
 * Billing section (org admins only). Shows the Subscription Details card — the plan total, Manage
 * Subscription (org payer only), and a Browse all plans link — above the four billing figures, split
 * out from the Members roster so each nav entry owns one concern. Reuses useMembersRosterData for the
 * billing figures and manage-subscription affordance (which also refreshes payment details on mount).
 * The standalone sidebar MemberRosterModal keeps its combined roster + billing view and is unaffected.
 */
export function BillingPanel() {
  // Billing is a dedicated billing surface, so force a fresh payment-details pull on open.
  const { isOrgAdmin, isError, billingStats, onManageSubscription } = useMembersRosterData({ forcePaymentRefresh: true })

  const seatCostCents = useAppSelector(selectDisplayCostPerSeatInCents)
  const subscriptionInterval = useAppSelector((state: RootState) => state.accountState.stripeSubscriptionInterval)
  const isLoading = useAppSelector((state: RootState) => state.accountState.loading)
  const isInitialLoadComplete = useAppSelector((state: RootState) => state.accountState.initialLoadComplete)
  // orgDetails (pending invites) and the payment-details fetch both back this card; surface either failure.
  const paymentError = useAppSelector((state: RootState) => state.accountState.error)
  const showLoader = isLoading && !isInitialLoadComplete

  // Defense in depth: the nav only surfaces Billing to org admins.
  if (!isOrgAdmin) return null

  // Recurring plan price = the same Paid seats × Seat cost shown in the grid below, so the card
  // reconciles with itself. (Deliberately not the upcoming-invoice amount, which also folds in
  // proration, credits, and temporary promos and would disagree with the figures beside it.)
  const paidSeats = billingStats?.paidSeats
  const planTotal =
    paidSeats != null && seatCostCents != null && subscriptionInterval
      ? `${formatCentsToDollarsCompact(paidSeats * seatCostCents)} / ${subscriptionInterval}`
      : '—'

  return (
    <SettingsPanelShell title="Billing">
      {showLoader ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <CircularProgressContinuous />
        </div>
      ) : isError || paymentError ? (
        <InlineMessage variant="error">Could not load billing details. Refresh the page and try again.</InlineMessage>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col overflow-hidden rounded-lg border border-brand-neutral-200 bg-white">
            <SectionHeader
              title="Subscription Details"
              trailing={
                <Button
                  variant="plain"
                  color="neutral"
                  size="xs"
                  trailingIcon={<OpenNew className="size-4" aria-hidden />}
                  onClick={() => window.open(kBrowseAllPlansUrl, '_blank', 'noopener,noreferrer')}
                >
                  Browse all plans
                </Button>
              }
            />
            <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <span className="font-rethink-sans text-base font-semibold leading-7 text-brand-neutral-950">Organization plan</span>
                <span className="text-sm tracking-[-0.15px] text-brand-neutral-800">{planTotal}</span>
              </div>
              {onManageSubscription && (
                <Button onClick={onManageSubscription} variant="tonal" color="neutral" size="sm" className="self-start sm:self-auto">
                  Manage Subscription
                </Button>
              )}
            </div>
          </div>

          {billingStats && <OrgBillingStats billingStats={billingStats} />}
        </div>
      )}
    </SettingsPanelShell>
  )
}
